import type {
  FooterTemplate,
  SocialLinkPlatform,
} from '@iheartjane/data-access';
import type { CustomThemeConfig, IconProps } from '@iheartjane/reefer';
import {
  Button,
  FacebookIcon,
  Flex,
  GoogleIcon,
  Image,
  InstagramIcon,
  Link,
  LinkedInIcon,
  ReeferThemeProvider,
  SpotifyIcon,
  TiktokIcon,
  Typography,
  XIcon,
  YelpIcon,
  YoutubeIcon,
  contrastingColor,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ComponentType } from 'react';
import { useMemo } from 'react';

import { bloomContrastColorKey } from '@jane/bloom/resources/theme';

import styles from './templatedFooter.module.css';

// If MAX_LINK_COLUMNS ever changes, the formula in the CSS file needs to be updated.
const MAX_LINK_COLUMNS = 3;
const WHITE_COLOR = '#FFFFFF';
const BLACK_COLOR = '#0E0E0E';
const CONTRAST_PALETTE = [BLACK_COLOR, WHITE_COLOR];

const SOCIAL_LINK_ICONS: Record<
  SocialLinkPlatform,
  ComponentType<Omit<IconProps, 'icon'>>
> = {
  facebook: FacebookIcon,
  google: GoogleIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  spotify: SpotifyIcon,
  tiktok: TiktokIcon,
  x: XIcon,
  yelp: YelpIcon,
  youtube: YoutubeIcon,
};

export const footerTheme = (
  colors: Record<string, string>
): CustomThemeConfig => {
  const backgroundColor =
    colors['background'] ||
    contrastingColor(colors['primary'] || BLACK_COLOR, CONTRAST_PALETTE) ||
    WHITE_COLOR;

  const primaryButtonColor =
    colors?.['primary'] ||
    contrastingColor(colors['background'] || WHITE_COLOR, CONTRAST_PALETTE) ||
    BLACK_COLOR;

  const textMain = contrastingColor(backgroundColor, CONTRAST_PALETTE);
  const textInverse = contrastingColor(primaryButtonColor, CONTRAST_PALETTE);

  return {
    colors: {
      background: backgroundColor,
      primary: primaryButtonColor,
      text: {
        inverse: textInverse,
        main: textMain,
      },
    },
    components: {
      Button: {
        variants: {
          minimal: {
            typographyColor: 'text-main',
          },
          primary: {
            typographyColor: bloomContrastColorKey(textInverse),
          },
        },
      },
    },
  };
};

export function TemplatedFooter({
  calls_to_action = [],
  colors = {},
  image,
  layout = 'align_left',
  links = [],
  mobile_layout,
  social_links = {},
  text_content,
}: Partial<FooterTemplate>) {
  const cta = calls_to_action[0];
  const isRightAligned = layout === 'align_right';
  const linksToRender = links.slice(0, MAX_LINK_COLUMNS);

  const theme = useMemo(() => footerTheme(colors), [colors]);

  return (
    <ReeferThemeProvider mode="client" theme={theme} themeId="premium-footer">
      <footer
        id="premium-footer"
        className={clsx(styles['templatedFooter'], {
          [styles['alignRight']]: isRightAligned,
        })}
      >
        <div className={styles['templatedFooter__layout']}>
          {image?.medium && (
            <Image
              altText="Logo"
              aspectRatio="16 / 10"
              className={styles['logoSection']}
              data-testid="logo-section"
              style={{
                '--logo-section-order':
                  (mobile_layout?.indexOf('image') ?? 3) + 1,
              }}
              objectFit="contain"
              src={image?.medium}
            />
          )}
          {cta && (
            <Flex
              flexDirection="column"
              gap={20}
              className={styles['ctaSection']}
              data-testid="cta-section"
              style={{
                '--cta-section-order':
                  (mobile_layout?.indexOf('calls_to_action') ?? 0) + 1,
              }}
            >
              {cta.header && (
                <Typography color="inherit" variant="h2-semibold" maxLines={2}>
                  {cta.header}
                </Typography>
              )}
              {cta.body && (
                <Typography color="inherit" maxLines={3}>
                  {cta.body}
                </Typography>
              )}
              {cta.button_label && cta.destination && (
                <Button
                  className={styles['ctaButton']}
                  href={cta.destination}
                  label={cta.button_label}
                  variant="primary"
                />
              )}
            </Flex>
          )}
          {social_links?.links && social_links.links.length > 0 && (
            <Flex
              flexDirection="column"
              gap={24}
              className={styles['socialLinksSection']}
              data-testid="social-links-section"
              style={{
                '--social-links-section-order':
                  (mobile_layout?.indexOf('social_links') ?? 1) + 1,
              }}
            >
              {social_links.label && (
                <Typography color="inherit" variant="h2-semibold" maxLines={1}>
                  {social_links.label}
                </Typography>
              )}
              <Flex gap={12} flexWrap="wrap">
                {social_links.links.map((link, index) => {
                  const Icon = SOCIAL_LINK_ICONS[link.label];
                  return (
                    <Button.Icon
                      icon={<Icon color="text-inverse" />}
                      href={link.destination}
                      key={index}
                      label={link.label}
                      variant="primary"
                    />
                  );
                })}
              </Flex>
            </Flex>
          )}
          {linksToRender.length > 0 &&
            linksToRender.map((link, index) => (
              <Flex
                className={styles['linksColumn']}
                data-testid="links-section"
                style={{
                  '--links-column-count': linksToRender.length,
                  '--links-column-index': index,
                  '--links-column-order':
                    (mobile_layout?.indexOf('links') ?? 2) + 1,
                }}
                key={index}
                flexDirection="column"
                alignItems={
                  linksToRender.length === 1 && layout === 'align_left'
                    ? 'flex-end'
                    : 'flex-start'
                }
                gap={16}
              >
                {link.destination && link.label && (
                  <Link
                    key={index}
                    color="inherit"
                    href={link.destination}
                    typography={link.links ? 'h2-semibold' : 'body-regular'}
                    variant={link.links ? 'minimal' : 'underline'}
                  >
                    {link.label}
                  </Link>
                )}
                {link.links &&
                  link.links.map((subLink, subIndex) => (
                    <Link
                      key={subIndex}
                      color="inherit"
                      href={subLink.destination || undefined}
                      typography="body-regular"
                    >
                      {subLink.label}
                    </Link>
                  ))}
              </Flex>
            ))}
          {text_content && (
            <div
              className={styles['textContent']}
              data-testid="text-content-section"
              style={{
                '--text-content-order':
                  (mobile_layout?.indexOf('text') ?? 4) + 1,
              }}
              dangerouslySetInnerHTML={{ __html: text_content }}
            />
          )}
        </div>
      </footer>
    </ReeferThemeProvider>
  );
}
